import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ArrowLeft, Save, Upload, Download, Trash2, FileIcon, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { RichEditor } from '../components/RichEditor';
import { ShareModal } from '../components/ShareModal';
import { ImageLightbox } from '../components/ImageLightbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AnalysisEditor = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const { selectedProject, user } = useApp();
  
  // Determine if this is a new analysis or editing an existing one
  // For route /analyses/new, routeId will be undefined (matched by /analyses/new route)
  // For route /analyses/:id, routeId will be the UUID
  const isNew = !routeId || routeId === 'new';
  const id = isNew ? 'new' : routeId;
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const fileInputRef = useRef(null);
  const autosaveTimeoutRef = useRef(null);
  const editorRef = useRef(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [publishedToPortal, setPublishedToPortal] = useState(false);
  
  // Track the last saved state to properly detect changes
  const lastSavedStateRef = useRef({ title: '', content: '', tags: [] });
  const isInitialLoadRef = useRef(true);
  
  // Image lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [selectedAltText, setSelectedAltText] = useState('');
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (id && id !== 'new') {
      isInitialLoadRef.current = true;
      fetchAnalysis(id);
      fetchAttachments(id);
    } else {
      // Reset for new analysis
      isInitialLoadRef.current = false;
      lastSavedStateRef.current = { title: '', content: '', tags: [] };
      setAnalysisData(null);
      setTitle('');
      setContent('');
      setTags([]);
      setAttachments([]);
      setLastSaved(null);
    }
  }, [id]);

  // Autosave effect
  useEffect(() => {
    if (hasChanges && id !== 'new' && !saving) {
      // Clear previous timeout
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }

      // Set new autosave timeout (30 seconds)
      autosaveTimeoutRef.current = setTimeout(() => {
        handleSave(true); // true = silent autosave
      }, 30000);
    }

    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [hasChanges, content, title, tags]);

  // Track changes - use lastSavedStateRef to properly detect changes
  // This avoids issues with HTML normalization from the editor
  useEffect(() => {
    // Skip change detection during initial load
    if (id !== 'new' && isInitialLoadRef.current) {
      return;
    }
    
    if (id === 'new') {
      // For new analyses, any content means there are changes
      setHasChanges(title.trim() !== '' || content.trim() !== '' || tags.length > 0);
    } else {
      // For existing analyses, compare with last saved state
      const savedState = lastSavedStateRef.current;
      const titleChanged = title !== savedState.title;
      const contentChanged = content !== savedState.content;
      const tagsChanged = JSON.stringify(tags) !== JSON.stringify(savedState.tags);
      
      setHasChanges(titleChanged || contentChanged || tagsChanged);
    }
  }, [title, content, tags, id]);

  // Add click handlers to images in the editor preview
  useEffect(() => {
    const handleImageClick = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedImageUrl(e.target.src);
        setSelectedAltText(e.target.alt || '');
        setIsLightboxOpen(true);
      }
    };

    // Find the editor content container
    const editorContainer = document.querySelector('.ProseMirror');
    if (editorContainer) {
      // Add pointer cursor style to images
      const images = editorContainer.querySelectorAll('img');
      images.forEach(img => {
        img.style.cursor = 'pointer';
        img.title = 'Clique para ampliar';
      });

      editorContainer.addEventListener('click', handleImageClick);

      return () => {
        editorContainer.removeEventListener('click', handleImageClick);
      };
    }
  }, [content]);

  /**
   * Normalize image URLs in content to use current API_URL.
   * This fixes broken images caused by domain changes between deployments.
   * Also handles relative URLs by converting them to absolute URLs for display.
   */
  const normalizeImageUrls = (htmlContent) => {
    if (!htmlContent) return '';
    
    let result = htmlContent;
    
    // Pattern 1: Match absolute URLs from any domain
    // Converts: https://any-domain/api/technical-analyses/files/uuid.ext
    // To: https://current-domain/api/technical-analyses/files/uuid.ext
    const absolutePattern = /src="https?:\/\/[^"]*\/api\/technical-analyses\/files\/([^"]+)"/g;
    result = result.replace(absolutePattern, `src="${API_URL}/api/technical-analyses/files/$1"`);
    
    // Pattern 2: Match relative URLs (start with /)
    // Converts: /api/technical-analyses/files/uuid.ext
    // To: https://current-domain/api/technical-analyses/files/uuid.ext
    const relativePattern = /src="\/api\/technical-analyses\/files\/([^"]+)"/g;
    result = result.replace(relativePattern, `src="${API_URL}/api/technical-analyses/files/$1"`);
    
    return result;
  };

  /**
   * Convert absolute URLs to relative URLs before saving.
   * This ensures content remains portable across different domains/environments.
   * 
   * Converts: https://any-domain/api/technical-analyses/files/uuid.ext
   * To: /api/technical-analyses/files/uuid.ext
   */
  const convertToRelativeUrls = (htmlContent) => {
    if (!htmlContent) return '';
    
    // Pattern to match absolute URLs pointing to our API
    const pattern = /src="https?:\/\/[^"]*\/api\/technical-analyses\/files\/([^"]+)"/g;
    
    // Replace with relative URL
    return htmlContent.replace(pattern, 'src="/api/technical-analyses/files/$1"');
  };

  const fetchAnalysis = async (analysisId) => {
    if (!analysisId || analysisId === 'new') {
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/technical-analyses/${analysisId}`);
      const data = response.data;
      setAnalysisData(data);
      setTitle(data.title);
      
      // Normalize image URLs to use current domain
      const normalizedContent = normalizeImageUrls(data.content || '');
      setContent(normalizedContent);
      
      setTags(data.tags || []);
      setPublishedToPortal(!!data.published_to_portal);
      setLastSaved(new Date(data.updated_at));
      
      // Store the initial saved state for change detection
      // Use normalized content to avoid false positives on change detection
      lastSavedStateRef.current = {
        title: data.title,
        content: normalizedContent,
        tags: data.tags || []
      };
      isInitialLoadRef.current = false;
    } catch (error) {
      console.error('Error fetching analysis:', error);
      if (error.response?.status === 404) {
        toast.error('Análise não encontrada');
        navigate('/analyses');
      } else {
        toast.error('Erro ao carregar análise');
      }
    }
  };

  const fetchAttachments = async (analysisId) => {
    if (!analysisId || analysisId === 'new') {
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/technical-analyses/${analysisId}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Erro ao carregar anexos:', error);
    }
  };

  const handleSave = async (silent = false) => {
    if (!title.trim()) {
      toast.error('O título é obrigatório');
      return;
    }

    if (!selectedProject?.id) {
      toast.error('Selecione um projeto antes de salvar');
      return;
    }

    setSaving(true);
    try {
      // Convert absolute URLs to relative before saving
      // This ensures content is portable across different domains/environments
      const contentToSave = convertToRelativeUrls(content || '');
      
      const payload = {
        title: title.trim(),
        content: contentToSave,
        tags: tags || [],
        project_id: selectedProject.id
      };

      let response;
      if (id === 'new') {
        response = await axios.post(`${API_URL}/api/technical-analyses`, payload);
        
        // Validate that we got a valid ID back
        const newId = response.data?.id;
        if (!newId) {
          throw new Error('Resposta inválida do servidor: ID não retornado');
        }
        
        // Update local state with the created analysis data
        setAnalysisData(response.data);
        setLastSaved(new Date(response.data.updated_at || response.data.created_at));
        
        // Update saved state ref for the new analysis
        lastSavedStateRef.current = {
          title: title.trim(),
          content: content || '',
          tags: tags || []
        };
        setHasChanges(false);
        
        if (!silent) {
          toast.success('Análise criada com sucesso!');
        }
        
        // Navigate to the edit URL with the new ID
        navigate(`/analyses/${newId}`, { replace: true });
      } else {
        response = await axios.put(`${API_URL}/api/technical-analyses/${id}`, payload);
        setAnalysisData(response.data);
        setLastSaved(new Date(response.data.updated_at));
        // Update saved state ref with current values (not response values to avoid normalization issues)
        lastSavedStateRef.current = {
          title: title.trim(),
          content: content || '',
          tags: tags || []
        };
        setHasChanges(false);
        if (!silent) {
          toast.success('Análise salva com sucesso!');
        }
      }
    } catch (error) {
      console.error('Error saving analysis:', error);
      // Show more detailed error message
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Erro ao salvar análise';
      toast.error(silent ? 'Erro no autosave' : errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (id === 'new') {
      toast.error('Salve a análise antes de adicionar anexos');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/technical-analyses/${id}/attachments`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setAttachments([...attachments, response.data]);
      toast.success('Arquivo enviado com sucesso!');

      // If it's an image, insert into editor using the editor's image node (not markdown text)
      if (file.type.startsWith('image/')) {
        const fullUrl = `${API_URL}${response.data.file_url}`;
        if (editorRef.current) {
          editorRef.current.insertImage(fullUrl, file.name);
        }
      }
    } catch (error) {
      toast.error('Erro ao enviar arquivo');
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageUpload = async (file) => {
    if (id === 'new') {
      toast.error('Salve a análise antes de adicionar imagens');
      throw new Error('Analysis must be saved first');
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API_URL}/api/technical-analyses/${id}/attachments`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      // Add to attachments list
      setAttachments(prev => [...prev, response.data]);
      
      // Return the full URL for the editor
      return `${API_URL}${response.data.file_url}`;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const insertAttachmentToEditor = (fileUrl, fileName) => {
    if (editorRef.current) {
      const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_URL}${fileUrl}`;
      editorRef.current.insertImage(fullUrl, fileName);
    } else {
      toast.error('Editor não encontrado');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Deseja excluir este anexo?')) return;

    try {
      await axios.delete(`${API_URL}/api/technical-analyses/attachments/${attachmentId}`);
      setAttachments(attachments.filter(att => att.id !== attachmentId));
      toast.success('Anexo excluído');
    } catch (error) {
      toast.error('Erro ao excluir anexo');
    }
  };

  const isImageFile = (fileName) => {
    const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
    const extension = fileName.split('.').pop()?.toLowerCase();
    return imageExtensions.includes(extension || '');
  };


  const handleDelete = async () => {
    if (id === 'new') return; // Can't delete unsaved analysis
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/technical-analyses/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Análise técnica excluída com sucesso');
      navigate('/analyses');
    } catch (error) {
      console.error('Error deleting analysis:', error);
      toast.error('Não foi possível excluir a análise técnica. Tente novamente.');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const getTimeSinceLastSave = () => {
    if (!lastSaved) return '';
    const seconds = Math.floor((Date.now() - lastSaved.getTime()) / 1000);
    if (seconds < 60) return `há ${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `há ${minutes}min`;
    const hours = Math.floor(minutes / 60);
    return `há ${hours}h`;
  };

  if (!selectedProject) {
    return <div className="text-center py-12 text-muted-foreground">Selecione um projeto</div>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {/* Top Bar */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => navigate('/analyses')} className="text-foreground border-border">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        
        <div className="flex items-center gap-4">
          {lastSaved && (
            <span className="text-sm text-muted-foreground">
              {hasChanges ? `Rascunho não salvo` : `Salvo ${getTimeSinceLastSave()}`}
            </span>
          )}
          
          {id !== 'new' && (
            <>
              <div className="flex items-center gap-2 pr-2 border-r border-border" title="Quando ativo, o cliente vê esta análise no portal">
                <Switch
                  checked={publishedToPortal}
                  onCheckedChange={async (val) => {
                    try {
                      const res = await axios.put(`${API_URL}/api/technical-analyses/${id}`, { published_to_portal: val });
                      setPublishedToPortal(!!res.data.published_to_portal);
                      toast.success(val ? 'Publicada no portal do cliente.' : 'Removida do portal do cliente.');
                    } catch (e) {
                      toast.error('Não foi possível atualizar a publicação.');
                    }
                  }}
                  data-testid="publish-portal-switch"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">Publicada no portal do cliente</span>
              </div>
              <Button 
                variant="outline"
                onClick={() => setShareModalOpen(true)}
                data-testid="share-button"
                className="text-foreground border-border"
              >
                <Share2 className="mr-2 h-4 w-4" />
                Compartilhar
              </Button>
              
              <Button 
                variant="ghost"
                onClick={() => setDeleteDialogOpen(true)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                data-testid="delete-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </Button>
            </>
          )}
          
          <Button 
            onClick={() => handleSave(false)} 
            disabled={saving || !hasChanges}
            data-testid="save-button"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>

      <Card className="bg-card border-border p-8">
        {/* Title */}
        <div className="space-y-2 mb-6">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título da análise técnica"
            className="text-4xl font-bold border-0 px-0 focus-visible:ring-0 placeholder:text-gray-300 text-foreground"
            data-testid="title-input"
          />
        </div>

        {/* Metadata */}
        <div className="space-y-3 mb-6 pb-4 border-b border-border">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">
              Projeto: <span className="font-medium text-foreground">{selectedProject.name}</span>
            </p>
            {analysisData && (
              <p className="text-sm text-muted-foreground">
                Última atualização em {new Date(analysisData.updated_at).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })} por {analysisData.author_name}
              </p>
            )}
          </div>

          {/* Tags Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-foreground shrink-0 w-[50px]">Tags:</label>
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Adicionar tag..."
                className="w-48"
                data-testid="tag-input"
              />
              <Button type="button" onClick={addTag} size="sm">
                Adicionar
              </Button>
            </div>
            
            {/* Tags display */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 ml-[58px]">
                {tags.map(tag => (
                  <Badge 
                    key={tag} 
                    className="gap-1 rounded-full bg-accent text-accent-foreground hover:bg-accent/80"
                  >
                    {tag}
                    <button 
                      onClick={() => removeTag(tag)} 
                      className="ml-1 hover:text-destructive transition-colors"
                      type="button"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Rich Editor */}
        <div className="mb-8">
          <RichEditor
            ref={editorRef}
            content={content}
            onChange={setContent}
            placeholder="Comece a escrever sua análise técnica..."
            onImageUpload={handleImageUpload}
          />
        </div>

        {/* Attachments Section */}
        <div className="border-t border-border pt-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-foreground">Anexos</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={id === 'new'}
              data-testid="upload-button"
            >
              <Upload className="mr-2 h-4 w-4" />
              Adicionar arquivo
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="file-input"
            />
          </div>

          {attachments.length > 0 ? (
            <div className="space-y-1.5">
              {attachments.map(attachment => {
                const isImage = isImageFile(attachment.file_name);
                return (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between px-3 py-2 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                    data-testid={`attachment-${attachment.id}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <FileIcon className={`h-4 w-4 shrink-0 ${isImage ? 'text-green-500' : 'text-primary'}`} />
                      <span className="text-sm font-medium text-foreground truncate">
                        {attachment.file_name}
                      </span>
                      {isImage && (
                        <span className="text-xs text-muted-foreground shrink-0">
                          Imagem
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 ml-3">
                      {isImage && (
                        <Button
                          size="sm"
                          onClick={() => insertAttachmentToEditor(attachment.file_url, attachment.file_name)}
                          data-testid={`insert-${attachment.id}`}
                          title="Inserir no conteúdo"
                          className="h-8 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                          Inserir no conteúdo
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`${API_URL}${attachment.file_url}`, '_blank')}
                        data-testid={`download-${attachment.id}`}
                        title="Baixar"
                        className="h-8 w-8 p-0"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAttachment(attachment.id)}
                        data-testid={`delete-${attachment.id}`}
                        title="Remover"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 border-2 border-dashed rounded-lg border-border">
              <FileIcon className="h-10 w-10 mx-auto mb-2 opacity-50 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {id === 'new' 
                  ? 'Salve a análise para adicionar anexos'
                  : 'Nenhum anexo adicionado ainda'
                }
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Share Modal */}
      <ShareModal 
        analysisId={id}
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
      />

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={isLightboxOpen}
        imageUrl={selectedImageUrl}
        altText={selectedAltText}
        onClose={() => setIsLightboxOpen(false)}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Análise Técnica</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta análise técnica? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
